import { addScript } from '@tunein/web-utils';
import get from 'lodash/get';
import keys from 'lodash/keys';
import { waitForConsent } from '../../../js/utils/consent';
import imaPlayerAdapter from '../../ads/media/players/ima/imaPlayerAdapter';
import logger from '../../logging/logger';
import { State, addStateChangeListener, getState, removeStateChangeListener, updateState, } from '../../utils/applicationState';
import audioVideoAdUtils from '../../utils/audioVideoAdUtils';
import { Networks } from '../../utils/constants';
import { getExperiment } from '../../utils/getExperiment';
import networkUrlBuilder from '../../utils/networkUrlBuilder';
import { ADSWIZZ_GDPR_TCFV2_TCSTRING_PARAMETER, ADSWIZZ_SYNCHROBOX_SCRIPT, ADSWIZZ_SYNCHROCLIENT_SCRIPT, DISCORD_ADSWIZZ_SYNCHROBOX_SCRIPT, DISCORD_ADSWIZZ_SYNCHROCLIENT_SCRIPT, MIDROLL_SLOT_NAME, PREROLL_SLOT_NAME, VIDEO_MEDIUM, } from '../constants';
import Events from '../media/Events';
import mediaEvents from '../media/mediaEvents';
import uamNetworkController from '../networks/uamNetworkController';
import isFirstInSessionController from './isFirstInSessionController';
const ADSWIZZ_SYNCHROBOX_SCRIPT_ID = 'ADSWIZZ_SYNCHROBOX_SCRIPT';
const ADSWIZZ_SYNCHROCLIENT_SCRIPT_ID = 'ADSWIZZ_SYNCHROCLIENT_SCRIPT';
const audioVideoAdController = {
    isImaPlayer: false,
    playSessionId: null,
    player: imaPlayerAdapter,
    slots: {},
    slot: null,
    async init() {
        this.player.init();
        await waitForConsent();
        await this.initScripts();
        const videoOnlyPrerollEnabled = getExperiment('ads.video.preroll.newFlow.enabled', false);
        const videoTamEnabled = getExperiment('ads.video.preroll.newFlow.tam.enabled', false);
        if (videoOnlyPrerollEnabled && videoTamEnabled) {
            uamNetworkController.fetchAndStoreApsParamsWithInterval(VIDEO_MEDIUM);
        }
    },
    async initScripts() {
        const { gdprApplies, tcString } = getState(State.gdprData);
        const ccpaData = getState(State.ccpaData, {});
        const isInDiscordIFrame = getState(State.isInDiscordIFrame);
        const queryParams = [`us_privacy=${ccpaData.usPrivacyString}`];
        if (gdprApplies) {
            queryParams.push(`${ADSWIZZ_GDPR_TCFV2_TCSTRING_PARAMETER}=${tcString}`);
        }
        const serializedQueryParams = queryParams.join('&');
        const adsWizzBoxScriptPromise = addScript({
            src: `${isInDiscordIFrame
                ? DISCORD_ADSWIZZ_SYNCHROBOX_SCRIPT
                : ADSWIZZ_SYNCHROBOX_SCRIPT}?${serializedQueryParams}`,
            id: ADSWIZZ_SYNCHROBOX_SCRIPT_ID,
        });
        const adsWizzClientScriptPromise = addScript({
            src: `${isInDiscordIFrame
                ? DISCORD_ADSWIZZ_SYNCHROCLIENT_SCRIPT
                : ADSWIZZ_SYNCHROCLIENT_SCRIPT}?${serializedQueryParams}`,
            id: ADSWIZZ_SYNCHROCLIENT_SCRIPT_ID,
        });
        await Promise.all([
            adsWizzBoxScriptPromise,
            adsWizzClientScriptPromise,
        ]).catch(() => { });
        updateState(State.isAdsWizzReady, true);
    },
    waitForAdsWizz() {
        return new Promise((resolve) => {
            function onAdsWizzReady(isAdsWizzReady) {
                if (isAdsWizzReady) {
                    removeStateChangeListener(State.isAdsWizzReady, onAdsWizzReady);
                    resolve();
                }
            }
            addStateChangeListener(State.isAdsWizzReady, onAdsWizzReady);
            onAdsWizzReady(getState(State.isAdsWizzReady));
        });
    },
    waitForVideoApsParams() {
        if (getState(State.isApsBidsRequestFinished)) {
            return;
        }
        return new Promise((resolve) => {
            function onApsBidsRequestFinished(isApsBidsRequestFinished) {
                if (isApsBidsRequestFinished) {
                    removeStateChangeListener(State.isApsBidsRequestFinished, onApsBidsRequestFinished);
                    resolve();
                }
            }
            addStateChangeListener(State.isApsBidsRequestFinished, onApsBidsRequestFinished);
        });
    },
    registerSlot(slot = {}) {
        if (![PREROLL_SLOT_NAME, MIDROLL_SLOT_NAME].includes(slot.name)) {
            return;
        }
        this.slots[slot.name] = slot;
        this.slot = slot;
    },
    deregisterSlot(slot) {
        this.player.deregisterSlot(slot);
        this.slot = null;
        delete this.slots[slot.name];
    },
    isRegistered(slot) {
        return this.slots[get(slot, 'name')] === slot;
    },
    canServeAd(slot) {
        const canServeAd = !audioVideoAdUtils.audioVideoAdDisabled();
        if (!canServeAd) {
            logger.debug(`${slot.name} ad is disabled.`);
        }
        return canServeAd;
    },
    serveNextAd(params) {
        const { nextSlot } = this.slot;
        this.registerSlot(nextSlot);
        return this.serveAd({ ...params, slot: nextSlot });
    },
    reportAdEligiblity({ slotName, isVideoEligible, isPlatformVideoEligible, isAudioEligible, isAudioPlatformEligible, }) {
        mediaEvents
            .event(Events.Type.audioVideoAd, Events.Status.eligibilityDecided)
            .emit({
            slotName,
            adType: VIDEO_MEDIUM,
            isEligible: isVideoEligible || false,
            isPlatformEligible: isPlatformVideoEligible || false,
        });
        mediaEvents
            .event(Events.Type.audioVideoAd, Events.Status.eligibilityDecided)
            .emit({
            slotName,
            adType: 'audio',
            isEligible: isAudioEligible || false,
            isPlatformEligible: isAudioPlatformEligible || false,
        });
    },
    async serveAd(params) {
        updateState(State.isServingAudioOrVideoAd, true);
        const nowPlaying = getState(State.nowPlaying);
        const canServeAdForSlot = this.canServeAd(params.slot);
        const isNewPlaybackSession = this.playSessionId !== getState(State.playSessionId);
        const prerollVideoEnabled = !!(nowPlaying?.ads?.canShowVideoPrerollAds &&
            getExperiment('ads.videoPreroll.enabled', true) &&
            isNewPlaybackSession);
        if (!(this.slots[params?.slot?.name] &&
            this.isRegistered(params.slot) &&
            canServeAdForSlot)) {
            this.reportAdEligiblity({
                slotName: params?.slot?.name,
                isVideoEligible: canServeAdForSlot && prerollVideoEnabled,
                isPlatformVideoEligible: nowPlaying?.ads?.canShowVideoPrerollAds,
                isAudioEligible: canServeAdForSlot,
                isAudioPlatformEligible: true,
            });
            updateState(State.isServingAudioOrVideoAd, false);
            return Promise.resolve();
        }
        await this.waitForAdsWizz();
        this.slot = params.slot;
        if (!this.player.isInitialized()) {
            await this.player.initialize();
        }
        const slotName = this.slot.name;
        const { adUnitId, appendPartnerAliasToAdUnitId, isVideoOnly } = this.slot.formatDefinition || {};
        if (slotName === PREROLL_SLOT_NAME &&
            this.slot.medium === VIDEO_MEDIUM &&
            !prerollVideoEnabled) {
            if (!this.slot.nextSlot) {
                this.reportAdEligiblity({
                    slotName,
                    isVideoEligible: false,
                    isPlatformVideoEligible: nowPlaying?.ads?.canShowVideoPrerollAds,
                    isAudioEligible: true,
                    isAudioPlatformEligible: true,
                });
                updateState(State.isServingAudioOrVideoAd, false);
                return;
            }
            updateState(State.isServingAudioOrVideoAd, true);
            return this.serveNextAd(params);
        }
        const partnerId = getState(State.partnerId);
        const ccpaData = getState(State.ccpaData, {});
        const usPrivacyString = ccpaData.usPrivacyString;
        const isUserInUS = getState(State.isUserInUS);
        const isOptedOutOfTargeting = getState(State.isOptedOutOfTargeting);
        const videoOnlyPrerollEnabled = getExperiment('ads.video.preroll.newFlow.enabled', false) &&
            isNewPlaybackSession;
        const midrollVideoEnabled = !!(getExperiment('ads.videoMidroll.enabled', false) &&
            get(global.window, 'document.visibilityState') === 'visible');
        const videoTamEnabled = getExperiment('ads.video.preroll.newFlow.tam.enabled', false);
        const canAttemptVideoPreroll = slotName === PREROLL_SLOT_NAME && prerollVideoEnabled;
        const canAttemptVideoMidroll = slotName === MIDROLL_SLOT_NAME && midrollVideoEnabled;
        const canAttemptVideo = canAttemptVideoPreroll || canAttemptVideoMidroll;
        const enableDoublePreroll = nowPlaying?.ads?.canShowDoublePrerollAds &&
            !(canAttemptVideo && videoOnlyPrerollEnabled);
        global.google?.ima?.settings?.setPpid?.(getState(State.ppid));
        this.reportAdEligiblity({
            slotName,
            isVideoEligible: videoOnlyPrerollEnabled && canAttemptVideo,
            isPlatformVideoEligible: nowPlaying?.ads?.canShowVideoPrerollAds,
            isAudioEligible: true,
            isAudioPlatformEligible: true,
        });
        if (isNewPlaybackSession) {
            this.playSessionId = getState(State.playSessionId);
        }
        let apsCustParams;
        if (isVideoOnly && videoTamEnabled) {
            await this.waitForVideoApsParams();
            apsCustParams = getState(State.videoApsCustParams);
            uamNetworkController.fetchAndStoreApsParamsWithInterval(VIDEO_MEDIUM);
        }
        const isFirstInSession = isFirstInSessionController.getIsFirstInSessionFlag(this.slot);
        let urlTag;
        const gamUrlBuilderParams = {
            adUnitId,
            appendPartnerAliasToAdUnitId,
            canAttemptVideo,
            usPrivacyString,
            slotName,
            isUserInUS,
            isOptedOutOfTargeting,
            enableDoublePreroll,
            isVideoOnly,
            apsCustParams,
            isFirstInSession,
        };
        if (params.slot.formatDefinition.adProvider === Networks.dfp && this.slot) {
            urlTag = networkUrlBuilder.buildDfpUrl(gamUrlBuilderParams);
        }
        else {
            urlTag = networkUrlBuilder.buildAdswizzUrl(partnerId, usPrivacyString, isUserInUS, isOptedOutOfTargeting, enableDoublePreroll, isFirstInSession);
        }
        params.urlTag = urlTag;
        logger.debug(`Ad Tag URL: ${urlTag}`);
        mediaEvents.event(Events.Type.audioVideoAd, Events.Status.loading).emit();
        updateState(State.isAudioOrVideoAdPlaying, true);
        await this.player.serveAd(params);
        if (this.slot.nextSlot) {
            await this.preload();
            await this.serveNextAd(params);
        }
        updateState(State.isAudioOrVideoAdPlaying, false);
        updateState(State.isServingAudioOrVideoAd, false);
    },
    releaseSlot() {
        return undefined;
    },
    async preload() {
        if (!keys(this.slots).length) {
            return;
        }
        await this.player.preload();
    },
    play(position) {
        this.player.play(position);
    },
    pause() {
        this.player.pause();
    },
    stop() {
        this.player.stop();
    },
    volume(volume) {
        this.player.volume(volume);
    },
    resize(width, height) {
        this.player.resize(width, height);
    },
};
export default audioVideoAdController;
